import { ArrowDown, ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { StatTileData } from '@/lib/system-intelligence'

const toneClass: Record<StatTileData['tone'], string> = {
  healthy: 'text-healthy',
  degraded: 'text-degraded',
  critical: 'text-critical-bright',
  neutral: 'text-fg',
}

/**
 * One headline figure on a system drill-down: a mono label, the value at display
 * size, and the incident count for the window pinned to the baseline beside it.
 *
 * The value carries the status color; the count is always red, since an incident
 * count is only ever worth surfacing when someone should look at it.
 */
export function StatTile({
  stat,
  compact,
  className,
}: {
  stat: StatTileData
  /**
   * The tile as tall as its content needs and no taller, for a page tuned to fit
   * a viewport. The roomy tile pads to a fixed 104px so a row of them lines up
   * even when one has no note; here the row is uniform anyway, so the padding is
   * pure height. Worth ~13px a row, which is what keeps Solo off a scrollbar.
   */
  compact?: boolean
  className?: string
}) {
  const { label, value, note, tone, delta } = stat
  // No incidents (or no incident count yet) means no badge at all — a "00" would
  // read as a measurement rather than as nothing having happened.
  const Arrow = delta?.direction === 'up' ? ArrowUp : ArrowDown

  return (
    <div
      className={cn(
        'border-line bg-surface rounded-lg border',
        compact ? 'p-4' : 'min-h-[104px] p-5',
        className,
      )}
    >
      <p className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
        {label}
      </p>
      <div className={cn('flex items-end justify-between gap-3', compact ? 'mt-2' : 'mt-4')}>
        <p className="flex min-w-0 items-baseline gap-2">
          <span className={cn('font-mono text-4xl leading-none font-bold', toneClass[tone])}>
            {value}
          </span>
          {note && <span className="text-fg-muted truncate font-mono text-base">{note}</span>}
        </p>
        {delta && (
          <span
            title={delta.title}
            className="text-critical-bright flex shrink-0 items-center gap-0.5 font-mono text-sm"
          >
            {delta.value}
            <Arrow className="size-3.5" />
          </span>
        )}
      </div>
    </div>
  )
}
